import { useState, useMemo, useEffect } from 'react'
import {
  Box, Typography, Chip, TextField, InputAdornment, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, Paper,
} from '@mui/material'
import { Label, Search } from '@mui/icons-material'
import ExportMenu from '../../components/common/ExportMenu'
import { useApi } from '../../hooks/useApi'
import { usePlantContext } from '../../hooks/usePlantContext'
import { formatNumber } from '../../utils/format'

const DEMO_DATA = [
  { item: '10001', description: 'HV CHILI BNS 15oz', briteCode: 'BC-1001', line: 'Line 1', abcClass: 'A', packSize: '24/15oz', casesPerPallet: 84, ecFactor: 1.00, minDOS: 60, maxDOS: 120, autoInv: true },
  { item: '10002', description: 'FINEST CHILI BNS 15oz', briteCode: 'BC-1002', line: 'Line 1', abcClass: 'A', packSize: '24/15oz', casesPerPallet: 84, ecFactor: 1.00, minDOS: 60, maxDOS: 120, autoInv: true },
  { item: '10005', description: 'HV CHILI NO BNS 15oz', briteCode: 'BC-1005', line: 'Line 1', abcClass: 'B', packSize: '24/15oz', casesPerPallet: 84, ecFactor: 1.00, minDOS: 45, maxDOS: 90, autoInv: true },
  { item: '10010', description: 'HV CHICKEN NOODLE 15oz', briteCode: 'BC-1010', line: 'Line 2', abcClass: 'A', packSize: '24/15oz', casesPerPallet: 84, ecFactor: 0.85, minDOS: 60, maxDOS: 120, autoInv: true },
  { item: '10011', description: 'HV TOMATO SOUP 10.75oz', briteCode: 'BC-1011', line: 'Line 2', abcClass: 'A', packSize: '24/10.75oz', casesPerPallet: 100, ecFactor: 0.72, minDOS: 60, maxDOS: 120, autoInv: true },
  { item: '10012', description: 'HV CREAM OF MUSHROOM 10.75oz', briteCode: 'BC-1012', line: 'Line 2', abcClass: 'B', packSize: '24/10.75oz', casesPerPallet: 100, ecFactor: 0.72, minDOS: 45, maxDOS: 90, autoInv: false },
  { item: '10020', description: 'HV PORK & BEANS 15oz', briteCode: 'BC-1020', line: 'Line 3', abcClass: 'A', packSize: '24/15oz', casesPerPallet: 84, ecFactor: 1.00, minDOS: 60, maxDOS: 120, autoInv: true },
  { item: '10021', description: 'HV BAKED BEANS 28oz', briteCode: 'BC-1021', line: 'Line 3', abcClass: 'B', packSize: '12/28oz', casesPerPallet: 60, ecFactor: 1.87, minDOS: 45, maxDOS: 90, autoInv: true },
  { item: '10030', description: 'GMI PROGRESSO CHKN 19oz', briteCode: 'BC-1030', line: 'Line 4', abcClass: 'A', packSize: '12/19oz', casesPerPallet: 72, ecFactor: 1.27, minDOS: 60, maxDOS: 120, autoInv: false },
  { item: '10031', description: 'GMI PROGRESSO MINESTRONE 19oz', briteCode: 'BC-1031', line: 'Line 4', abcClass: 'B', packSize: '12/19oz', casesPerPallet: 72, ecFactor: 1.27, minDOS: 45, maxDOS: 90, autoInv: false },
]

export default function ItemData() {
  const { plant } = usePlantContext()
  const { error, get } = useApi()
  const [data, setData] = useState(DEMO_DATA)
  const [search, setSearch] = useState('')
  const [orderBy, setOrderBy] = useState('item')
  const [order, setOrder] = useState('asc')

  useEffect(() => {
    let cancelled = false
    get(`/plants?plant=${plant.plantCode}`)
      .then((result) => {
        if (cancelled) return
        if (result?.items?.length) {
          setData(result.items.map((it) => ({
            item: it.itemNumber || it.item || '',
            description: it.description || '',
            briteCode: it.briteCode || '',
            line: it.line || '',
            abcClass: it.abcClass || '',
            packSize: it.packSize || '',
            casesPerPallet: it.casesPerPallet || 0,
            ecFactor: it.ecFactor || 0,
            minDOS: it.minDOS || 0,
            maxDOS: it.maxDOS || 0,
            autoInv: it.autoInv ?? false,
          })))
        }
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [plant.plantCode]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = (col) => {
    setOrder(orderBy === col && order === 'asc' ? 'desc' : 'asc')
    setOrderBy(col)
  }

  const rows = useMemo(() => {
    let filtered = data.filter((r) =>
      `${r.item} ${r.description} ${r.briteCode} ${r.line} ${r.abcClass}`.toLowerCase().includes(search.toLowerCase()),
    )
    filtered.sort((a, b) => {
      const av = a[orderBy], bv = b[orderBy]
      if (av == null || bv == null) return 0
      const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv
      return order === 'asc' ? cmp : -cmp
    })
    return filtered
  }, [data, search, orderBy, order])

  const columns = [
    { id: 'item', label: 'Item #' },
    { id: 'description', label: 'Description' },
    { id: 'briteCode', label: 'Brite Code' },
    { id: 'line', label: 'Line' },
    { id: 'abcClass', label: 'ABC Class' },
    { id: 'packSize', label: 'Pack Size' },
    { id: 'casesPerPallet', label: 'Cases/Pallet', align: 'right' },
    { id: 'ecFactor', label: 'EC Factor', align: 'right' },
    { id: 'minDOS', label: 'Min DOS', align: 'right' },
    { id: 'maxDOS', label: 'Max DOS', align: 'right' },
    { id: 'autoInv', label: 'Auto-Inv' },
  ]

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Label color="primary" />
        <Typography variant="h3" sx={{ flex: 1 }}>Item Data</Typography>
        <ExportMenu />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip label="SAP BI · Master Data" size="small" variant="outlined" />
        <Typography variant="overline" color="text.secondary">
          As of Mar 10, 2026 · v6 · {data.length} items
        </Typography>
      </Box>

      {error && (
        <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>Using demo data — API unavailable</Alert>
      )}

      <TextField
        size="small"
        placeholder="Search items…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, width: 280 }}
        slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> } }}
      />

      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 1100 }}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.id} align={col.align || 'left'} sx={{ fontWeight: 700 }}>
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.item} hover>
                <TableCell sx={{ fontWeight: 600 }}>{row.item}</TableCell>
                <TableCell>{row.description}</TableCell>
                <TableCell>{row.briteCode}</TableCell>
                <TableCell>{row.line}</TableCell>
                <TableCell>
                  <Chip
                    label={row.abcClass}
                    size="small"
                    color={row.abcClass === 'A' ? 'primary' : 'default'}
                    variant={row.abcClass === 'A' ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 700, minWidth: 28 }}
                  />
                </TableCell>
                <TableCell>{row.packSize}</TableCell>
                <TableCell align="right">{formatNumber(row.casesPerPallet)}</TableCell>
                <TableCell align="right">{row.ecFactor.toFixed(2)}</TableCell>
                <TableCell align="right">{row.minDOS}</TableCell>
                <TableCell align="right">{row.maxDOS}</TableCell>
                <TableCell>{row.autoInv ? 'Yes' : 'No'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
